import '../../i18n/autoTranslate';
import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";

const FilterSidebar = ({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  onClearFilters,
  categories,
  activeCategory,
  onCategoryChange,
  className = "",
}) => {
  const filterSections = [
    {
      id: "category",
      title: "Resource Type",
      icon: "Grid3X3",
      options: categories || [],
    },
    {
      id: "industry",
      title: "Industry",
      icon: "Building",
      options: [
        { id: "tech", label: "Technology", count: 14 },
        { id: "finance", label: "Finance", count: 1 },
        { id: "healthcare", label: "Healthcare", count: 0 },
        { id: "marketing", label: "Marketing", count: 0 },
        { id: "sales", label: "Sales", count: 0 },
        { id: "consulting", label: "Consulting", count: 1 },
      ],
    },
    {
      id: "role",
      title: "Role",
      icon: "Briefcase",
      options: [
        { id: "Full Stack Developer", label: "Full Stack Developer", count: 2 },
        { id: "Frontend Developer", label: "Frontend Developer", count: 2 },
        { id: "Backend Developer", label: "Backend Developer", count: 2 },
        { id: "Product Manager", label: "Product Manager", count: 1 },
        { id: "Data Analyst", label: "Data Analyst", count: 1 },
        { id: "DevOps Engineer", label: "DevOps Engineer", count: 1 },
        { id: "QA Engineer", label: "QA Engineer", count: 1 },
        { id: "UX Designer", label: "UX Designer", count: 1 },
      ],
    },
    {
      id: "interviewType",
      title: "Interview Type",
      icon: "MessageSquareText",
      options: [
        { id: "technical", label: "Technical", count: 5 },
        { id: "behavioral", label: "Behavioral", count: 2 },
        { id: "system design", label: "System Design", count: 3 },
        { id: "case", label: "Case Interview", count: 3 },
        { id: "product case", label: "Product Case", count: 1 },
        { id: "testing", label: "Testing", count: 1 },
        { id: "mixed", label: "Mixed", count: 2 },
      ],
    },
    {
      id: "skillLevel",
      title: "Skill Level",
      icon: "TrendingUp",
      options: [
        { id: "beginner", label: "Beginner", count: 3 },
        { id: "intermediate", label: "Intermediate", count: 9 },
        { id: "advanced", label: "Advanced", count: 5 },
      ],
    },
    {
      id: "duration",
      title: "Duration",
      icon: "Clock",
      options: [
        { id: "short", label: "Under 15 min", count: 167 },
        { id: "medium", label: "15-30 min", count: 198 },
        { id: "long", label: "30-60 min", count: 134 },
        { id: "extended", label: "Over 1 hour", count: 89 },
      ],
    },
    {
      id: "features",
      title: "Features",
      icon: "Sparkles",
      options: [
        { id: "offline", label: "Offline Available", count: 234 },
        { id: "interactive", label: "Interactive", count: 156 },
        { id: "ai-powered", label: "AI-Powered", count: 123 },
        { id: "premium", label: "Premium Content", count: 89 },
      ],
    },
  ];

  const handleFilterToggle = (sectionId, optionId) => {
    const currentFilters = filters?.[sectionId] || [];
    const newFilters = currentFilters?.includes(optionId)
      ? currentFilters?.filter((id) => id !== optionId)
      : [...currentFilters, optionId];

    onFilterChange(sectionId, newFilters);
  };

  const handleCategorySelect = (category) => {
    onCategoryChange(category);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.reduce(
      (total, filterArray) => total + filterArray?.length,
      0
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <div
        className={`
  fixed lg:relative top-0 right-0 h-[90vh] lg:h-full w-80 lg:w-full rounded-3xl
  glass transform transition-transform duration-300 ease-smooth z-50 lg:z-auto
  overflow-y-auto scrollbar-thin scrollbar-thumb-white/15 scrollbar-track-transparent
  ${isOpen ? "translate-x-0" : "translate-x-full "}
  ${className}
`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} className="text-white" />
            <h3 className="text-lg font-semibold text-white">Filters</h3>
            {getActiveFilterCount() > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-white/10 text-white rounded-full border border-white/10">
                {getActiveFilterCount()}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-white/80 hover:text-white rounded-2xl"
            >
              Clear All
            </Button>

            <button
              onClick={onClose}
              className="md:hidden w-9 h-9 rounded-2xl bg-white/5 hover:bg-white/10 text-white/80 hover:text-white transition-colors duration-150 flex items-center justify-center border border-white/10"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Filter Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {filterSections?.map((section) => (
            <div key={section?.id} className="space-y-3">
              {/* Section Header */}
              <div className="flex items-center space-x-2">
                <Icon
                  name={section?.icon}
                  size={16}
                  className="text-white/70"
                />
                <h4 className="font-medium text-white/80">{section?.title}</h4>
              </div>

              {/* Section Options */}
              <div className="space-y-2 pl-6">
                {section?.options?.map((option) => (
                  <div
                    key={option?.id}
                    className="flex items-center justify-between"
                  >
                    {section.id === "category" ? (
                      <button
                        onClick={() => handleCategorySelect(option)}
                        className={`flex items-center justify-between w-full p-2.5 rounded-2xl transition-all duration-200 ${
                          activeCategory?.id === option.id
                            ? "bg-white/10 text-white border border-white/15"
                            : "text-white/80 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/10"
                        }`}
                      >
                        <span className="font-medium">{option.name}</span>
                        <span
                          className={`text-sm ${
                            activeCategory?.id === option.id
                              ? "text-white/70"
                              : "text-white/50"
                          }`}
                        >
                          {option.count}
                        </span>
                      </button>
                    ) : (
                      <>
                        <Checkbox
                          label={option?.label}
                          checked={(filters?.[section?.id] || [])?.includes(
                            option?.id
                          )}
                          onChange={() =>
                            handleFilterToggle(section?.id, option?.id)
                          }
                          size="sm"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Actions */}
        <div className="p-5 border-t border-white/10">
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              className="flex-1 border-white/10 text-white/80 hover:text-white hover:bg-white/5 rounded-2xl"
            >
              Reset
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={onClose}
              className="flex-1 rounded-2xl"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;
